import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router";
import { ErrorBanner } from "@/components/ErrorBanner";
import ChartVisualConfig from "@/components/settings/ChartVisualConfig";
import WidgetConfig, { type StatisticType } from "@/components/WidgetConfig";
import { AddWidgetModal } from "@/components/dashboard/AddWidgetModal";
import DataSettings from "@/components/DataSettings";
import { PlaygroundType } from "@/services/api/widget/types";
import { usePlayground } from "@/context/PlaygroundContext";
import isEqual from "lodash/isEqual";
import { updateFavouriteOrder, type ResourceList } from "@/services/api/playground";
import { ConfirmDeleteDialog } from "@/components/ui/dialog/ConfirmDeleteDialog";

// Import our custom hooks
import { useAssetManager } from "./hooks/useAssetManager";
import { useVisualSettings } from "./hooks/useVisualSettings";
import { usePlaygroundSettings, type ChartSettings } from "./hooks/usePlaygroundSettings";
import { useUrlParameters } from "./hooks/useUrlParameters";
import { usePlaygroundData } from "./hooks/usePlaygroundData";
import { useExposureBands } from "./hooks/useExposureBands";
import { extractExposureCategories } from "./adapters/echarts/exposureColumnsEChart";
import type { ExposureBandsMap } from "./adapters/echarts/types";
import {
  useScreenerState,
  type SelectedItem,
} from "@/components/screener/hooks/useScreenerState";
import { useNotification } from "@/context/NotificationContext";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { WORKBENCH_INSET_PANEL_CLASS, WORKBENCH_SURFACE_CLASS } from "@/components/ui/workbench";
import { REPORT_BUILDER_PANEL_TOGGLE_CLASS } from "@/components/common/report-builder/reportBuilderSurface";
import { useAuth } from "@/context/AuthContext";

// Import sub-components
import { ChartArea } from "./components/ChartArea";
import { StatisticsTableArea } from "./components/StatisticsTableArea";
import { CorrelationMatrixArea } from "./components/CorrelationMatrixArea";
import { ResourceListSection } from "./ResourceListSection";
import { AddResourceListDialog } from "./ResourceListDialog";
import { useResourceListManager } from "./hooks/useResourceListManager";
import { Button } from "@/components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronLeft,
  faChevronRight,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { motion, AnimatePresence } from "framer-motion";

import { PLAYGROUND_PERIODS as periods } from "./periodConfig";
import { isPlaygroundTypeAvailable } from "./playgroundAvailability";

export { PLAYGROUND_PERIODS as periods } from "./periodConfig";

export interface PlaygroundProps {
  initialAssets?: string[];
  assetDisplayNames?: Record<string, string>;
  initialAssetMetadata?: Record<string, import("./hooks/useAssetManager").ExtendedAssetInfo>;
  showAssetSearch?: boolean;
  allowAssetToggle?: boolean;
  className?: string;
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
  defaultResourceType?: "product" | "portfolio" | "benchmark" | "index";
  hideTabs?: boolean;
  initialChartSettings?: Partial<ChartSettings>;
}

export const Playground: React.FC<PlaygroundProps> = ({
  initialAssets = [],
  assetDisplayNames: initialAssetDisplayNames = {},
  initialAssetMetadata = {},
  showAssetSearch = true,
  allowAssetToggle = true,
  className = "",
  isSearchOpen,
  setIsSearchOpen,
  defaultResourceType = "product",
  hideTabs = false,
  initialChartSettings = {},
}) => {
  // Get the playground context for type management
  const { config, setPlaygroundType } = usePlayground();
  const { user } = useAuth();

  // Screener state hook for "Send to Screener" functionality
  const { setQuickAnalysisItems, enterQuickAnalysisMode } = useScreenerState();

  // Notification hook for user feedback
  const { showSuccess } = useNotification();

  // Navigation hook for redirecting to screener
  const navigate = useNavigate();

  // OS mode actions for opening windows
  const osActions = useOsActionsOptional();

  // State for search handling
  const handleSetSearchOpen = useCallback(
    (value: boolean) => {
      setIsSearchOpen(value);
    },
    [setIsSearchOpen]
  );

  // Basic component state
  const [isVisualSettingsOpen, setIsVisualSettingsOpen] = useState(false);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [isRightPanelMinimized, setIsRightPanelMinimized] = useState(false);

  // Toggle right panel visibility
  const handleRightPanelToggle = useCallback(() => {
    setIsRightPanelMinimized((prev) => !prev);
  }, []);

  // Resource list dialog states
  const [isResourceListDialogOpen, setIsResourceListDialogOpen] =
    useState(false);
  const [newResourceListName, setNewResourceListName] = useState("");
  const [useCurrentResources, setUseCurrentResources] = useState(true);
  const addResourceListInputRef = useRef<HTMLInputElement>(null);

  // Initialize our custom hooks
  const {
    settings: chartSettings,
    setPeriod,
    setCustomDateRange,
    setDisplayMode,
    setRebaseValue,
    setFrequency,
    setStatistic,
    setWindowSize,
    setMetricFieldPath,
    setExposureType,
    setExposureDisplayMode,
    setXStatistic,
    setYStatistic,
    setXLabel,
    setYLabel,
    setColumns,
    setShowBenchmarks,
    setExtendIncompleteWithProxy,
    setRiskFreeRate,
    setMultipleSettings,
  } = usePlaygroundSettings();

  const appliedInitialChartSettings = useRef(false);
  useEffect(() => {
    if (appliedInitialChartSettings.current || Object.keys(initialChartSettings).length === 0) {
      return;
    }
    setMultipleSettings(initialChartSettings);
    appliedInitialChartSettings.current = true;
  }, [initialChartSettings, setMultipleSettings]);

  const handleSetExposureType = useCallback(
    (type: string) => {
      setExposureType(
        type as "asset_allocation" | "sector" | "geography" | "currency"
      );
    },
    [setExposureType]
  );

  const {
    settings: visualSettings,
    updateSetting: updateVisualSetting,
    resetToDefaults: resetVisualSettingsToDefaults,
  } = useVisualSettings();

  const {
    selectedAssets,
    assetDisplayNames,
    assetMetadata,
    assetColors,
    toggleAsset,
    setAssets,
    setAssetMetadata,
    setAssetColor,
  } = useAssetManager({
    initialAssets,
    initialDisplayNames: initialAssetDisplayNames,
    initialAssetMetadata,
    defaultResourceType,
    allowAssetToggle,
  });

  const {
    data,
    assetStats,
    isLoading,
    error: dataError,
    accessDeniedIds,
    fetchPlaygroundData,
  } = usePlaygroundData({ defaultResourceType });

  const {
    bands: exposureBands,
    saveBands: saveExposureBands,
    clearBands: clearExposureBands,
  } = useExposureBands(user?.id, chartSettings.exposureType || "asset_allocation");

  const exposureCategories = useMemo(
    () =>
      config.type === PlaygroundType.ExposureColumns
        ? extractExposureCategories(data)
        : [],
    [config.type, data]
  );

  const handleSaveExposureBands = useCallback(
    (bands: ExposureBandsMap) => saveExposureBands(exposureCategories, bands),
    [saveExposureBands, exposureCategories]
  );

  // Resource list manager hook
  const {
    resourceLists,
    isLoading: isResourceListsLoading,
    activeResourceListId,
    createResourceListFromSelection,
    loadResourceList,
    deleteResourceListById,
    clearActiveResourceList,
    updateResourceListOrderHandler,
    updateResourceListIcon,
    updateResourceListColor,
    toggleFavourite,
    updateResourceList,
    setActiveResourceListIdDirectly,
  } = useResourceListManager({
    selectedAssets,
    assetMetadata,
    onLoadResourceList: setAssets,
  });

  // Track the original loaded resource list
  const [originalResourceList, setOriginalResourceList] =
    useState<ResourceList | null>(null);

  // Track if we've loaded from URL params
  const [hasAutoLoadedFirstResourceList, setHasAutoLoadedFirstResourceList] =
    useState(false);
  const [paramsLoaded, setParamsLoaded] = useState(false);
  const hadUrlParamsOnLoad = useRef(window.location.search.length > 0);

  // Get current location to detect navigation
  const location = useLocation();

  // Detect navigation with new URL params and update hadUrlParamsOnLoad
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasAssetsParam = searchParams.has("assets");

    // If we have assets in URL, we're navigating from Screener
    if (hasAssetsParam) {
      hadUrlParamsOnLoad.current = true;
      setParamsLoaded(false); // Reset to allow re-processing
    }
  }, [location.search]);

  // Add state for dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // When a resource list is loaded, store its state as the original
  const handleSelectResourceList = useCallback(
    (resourceListId: string | null) => {
      if (resourceListId) {
        const resourceList = resourceLists.find(
          (list) => list.id === resourceListId
        );
        if (resourceList) {
          setOriginalResourceList(resourceList);
          loadResourceList(resourceListId);
        }
      } else {
        setOriginalResourceList(null);
        clearActiveResourceList();
      }
    },
    [resourceLists, loadResourceList, clearActiveResourceList]
  );

  // Check if resource list has been modified
  const isResourceListDirty = useMemo(() => {
    if (!originalResourceList) return false;
    const originalAssetIds = originalResourceList.items.map(
      (item) => item.resource_id
    );
    return !isEqual(selectedAssets, originalAssetIds);
  }, [originalResourceList, selectedAssets]);

  // Update handler
  const handleUpdateResourceList = useCallback(async () => {
    if (!originalResourceList) return;
    // Build updated resource list object
    const updatedResourceList = {
      ...originalResourceList,
      items: selectedAssets.map((assetId: string, index: number) => {
        const metadata = assetMetadata[assetId];
        return {
          resource_type: metadata?.resourceType || "product",
          resource_id: metadata?.resourceId || assetId,
          stream_type: metadata?.streamType || "default",
          display_name: metadata?.displayName || assetId,
          position: index + 1,
        };
      }),
      // Optionally add chartSettings or other fields here
    };
    await updateResourceList(updatedResourceList); // use the hook's update function
    setOriginalResourceList(updatedResourceList); // reset dirty state
  }, [originalResourceList, selectedAssets, assetMetadata, updateResourceList]);

  // Handle playground type changes for URL parameter loading
  const handlePlaygroundTypeChange = useCallback(
    (type: PlaygroundType) => {
      if (!isPlaygroundTypeAvailable(user?.company_id, type)) {
        return;
      }

      setPlaygroundType(type);

      // When switching to Exposure type, ensure we have a default exposure type set
      if (
        (type === PlaygroundType.Exposure ||
          type === PlaygroundType.ExposureColumns ||
          type === PlaygroundType.ExposureOverTime) &&
        !chartSettings.exposureType
      ) {
        setExposureType("asset_allocation");
      }

      if (type === PlaygroundType.ExposureOverTime) {
        setFrequency("quarterly");
      }
    },
    [setPlaygroundType, chartSettings.exposureType, setExposureType, setFrequency, user?.company_id],
  );

  useEffect(() => {
    if (!isPlaygroundTypeAvailable(user?.company_id, config.type)) {
      setPlaygroundType(PlaygroundType.PerformanceHistory);
    }
  }, [user?.company_id, config.type, setPlaygroundType]);

  // Handle sending current assets to Screener Quick Analysis
  const handleSendToScreener = useCallback(() => {
    if (selectedAssets.length === 0) return;

    // Create SelectedItem array from current assets for Quick Analysis
    const items: SelectedItem[] = selectedAssets.map((assetId) => {
      const meta = assetMetadata[assetId];
      return {
        id: assetId,
        display_name: meta?.displayName || assetId,
        resource_type: meta?.resourceType || "product",
        resource_id: meta?.resourceId || assetId,
        default_stream_type: meta?.streamType || "default",
        pinnedAt: Date.now(),
      };
    });

    // Set the Quick Analysis items and enter the mode
    setQuickAnalysisItems(items);
    enterQuickAnalysisMode();

    // Show success notification
    showSuccess(
      `Sent ${selectedAssets.length} resource${selectedAssets.length === 1 ? "" : "s"} to Screener Quick Analysis`
    );

    // Navigate to Screener (or open window in OS mode)
    if (osActions) {
      osActions.openWindow({
        appId: "screener",
        title: "Screener",
        params: {},
      });
    } else {
      navigate("/screener");
    }
  }, [
    selectedAssets,
    assetMetadata,
    setQuickAnalysisItems,
    enterQuickAnalysisMode,
    showSuccess,
    navigate,
    osActions,
  ]);

  // Load URL parameters
  useUrlParameters({
    onUrlParamsLoaded: (params) => {
      // Apply loaded params to our state
      if (params.playgroundType) {
        handlePlaygroundTypeChange(params.playgroundType);
      }
      if (params.assets && params.assets.length > 0) {
        setAssets(params.assets, params.assetMetadata);
      } else if (params.assetMetadata) {
        setAssetMetadata(params.assetMetadata);
      }
      if (params.chartSettings) {
        setMultipleSettings(params.chartSettings);
      }
      // Now clear the URL after params are loaded and applied
      window.history.replaceState({}, document.title, window.location.pathname);
      setParamsLoaded(true);
    },
  });

  // Load first resource list when component mounts and no initial assets provided
  useEffect(() => {
    const loadFirstResourceList = async () => {
      // Only load first resource list if:
      // 1. No initial assets were provided
      // 2. No assets are currently selected (empty playground)
      // 3. Preferences haven't been loaded before (prevent reloading when user clears selection)
      // 4. Resource lists are available and not loading
      // 5. No URL parameters were present on load (otherwise URL params should be used)
      if (
        initialAssets.length === 0 &&
        selectedAssets.length === 0 &&
        !preferencesLoaded &&
        !isResourceListsLoading &&
        resourceLists.length > 0 &&
        !hadUrlParamsOnLoad.current
      ) {
        try {
          // Load the first resource list automatically
          const firstResourceList = resourceLists[0];
          console.log(
            `Auto-loading first resource list: ${firstResourceList.name}`
          );
          // Set original resource list for dirty state tracking
          setOriginalResourceList(firstResourceList);
          // Load the resource list content
          loadResourceList(firstResourceList.id!);
          // Directly set active ID as backup to ensure UI state is correct
          setActiveResourceListIdDirectly(firstResourceList.id!);
          // Mark preferences as loaded regardless of whether assets were found
          setPreferencesLoaded(true);
        } catch (error) {
          console.error("Error loading first resource list:", error);
          // Still mark as loaded to prevent retrying
          setPreferencesLoaded(true);
        }
      } else if (
        initialAssets.length === 0 &&
        selectedAssets.length === 0 &&
        !preferencesLoaded &&
        !isResourceListsLoading &&
        resourceLists.length === 0 &&
        !hadUrlParamsOnLoad.current
      ) {
        // No resource lists available, just mark preferences as loaded
        setPreferencesLoaded(true);
      }
    };

    loadFirstResourceList();
  }, [
    initialAssets.length,
    selectedAssets.length,
    preferencesLoaded,
    isResourceListsLoading,
    resourceLists,
    loadResourceList,
    setActiveResourceListIdDirectly,
    setAssets,
  ]);

  // Load first resource list if there are no URL params, no initial assets, and at least one resource list
  useEffect(() => {
    if (
      paramsLoaded &&
      !hadUrlParamsOnLoad.current &&
      initialAssets.length === 0 && // Don't auto-load if we have initial assets
      resourceLists.length > 0 &&
      !originalResourceList &&
      !hasAutoLoadedFirstResourceList
    ) {
      const firstResourceList = resourceLists[0];
      setOriginalResourceList(firstResourceList);
      loadResourceList(firstResourceList.id!);
      // Directly set active ID as backup to ensure UI state is correct
      setActiveResourceListIdDirectly(firstResourceList.id!);
      setHasAutoLoadedFirstResourceList(true);
    }
  }, [
    paramsLoaded,
    initialAssets.length,
    resourceLists,
    originalResourceList,
    hasAutoLoadedFirstResourceList,
    loadResourceList,
    setActiveResourceListIdDirectly,
  ]);

  // Visual settings handlers
  const openVisualSettings = () => setIsVisualSettingsOpen(true);
  const closeVisualSettings = () => setIsVisualSettingsOpen(false);

  // Resource list handlers
  const handleCreateResourceList = () => {
    setIsResourceListDialogOpen(true);
  };

  const handleEditResourceList = (resourceListId: string) => {
    const resourceList = resourceLists.find(
      (list) => list.id === resourceListId
    );
    if (resourceList) {
      setNewResourceListName(resourceList.name);
      setIsResourceListDialogOpen(true);
    }
  };

  const handleDeleteResourceList = (resourceListId: string) => {
    setPendingDeleteId(resourceListId);
    setDeleteDialogOpen(true);
  };

  const handleAddResourceList = async () => {
    if (newResourceListName.trim()) {
      // If useCurrentResources is true, use current selection, otherwise use empty array
      const resourcesToUse = useCurrentResources ? selectedAssets : [];
      const metadataToUse = useCurrentResources ? assetMetadata : {};

      const newResourceList = await createResourceListFromSelection(
        newResourceListName.trim(),
        "",
        resourcesToUse,
        metadataToUse
      );
      if (newResourceList && newResourceList.id) {
        // Set the original resource list to track changes
        setOriginalResourceList(newResourceList);
        // Load the resource list directly using the newly created resource list
        loadResourceListDirectly(newResourceList);
      }
      setNewResourceListName("");
      setUseCurrentResources(true);
      setIsResourceListDialogOpen(false);
    }
  };

  // Load a resource list directly without relying on state
  const loadResourceListDirectly = useCallback(
    (resourceList: ResourceList) => {
      // Convert resource list items back to asset format
      const assets = resourceList.items.map((item) => item.resource_id);
      const metadata: Record<string, any> = {};

      resourceList.items.forEach((item) => {
        metadata[item.resource_id] = {
          displayName: item.display_name,
          resourceId: item.resource_id,
          resourceType: item.resource_type,
          streamType: "default", // Always use default to let backend resolve appropriate stream type
        };
      });

      setAssets(assets, metadata);

      // Also set the active resource list ID in the resource list manager
      if (resourceList.id) {
        // Set the original resource list for dirty state tracking
        setOriginalResourceList(resourceList);
        // Directly set the active resource list ID
        setActiveResourceListIdDirectly(resourceList.id);
      }
    },
    [setAssets, setActiveResourceListIdDirectly]
  );

  // Handle favourite order updates
  const handleUpdateFavouriteOrder = useCallback(async (order: string[]) => {
    const success = await updateFavouriteOrder(order);
    if (!success) {
      console.error("Failed to update favourite order");
    }
  }, []);

  // Fetch data when selectedAssets or chartSettings change
  useEffect(() => {
    if (selectedAssets.length > 0) {
      fetchPlaygroundData(selectedAssets, assetMetadata, chartSettings);
    }
  }, [selectedAssets, assetMetadata, chartSettings, fetchPlaygroundData]);

  const handleUpdateResourceListField = useCallback(
    (id: string, updates: Partial<ResourceList>) => {
      const list = resourceLists.find((l) => l.id === id);
      if (!list) return;
      updateResourceList({ ...list, ...updates });
    },
    [resourceLists, updateResourceList]
  );

  return (
    <div className={`h-full relative flex flex-col ${className}`}>
      {dataError && (
        <ErrorBanner message={dataError.message || "An error occurred"} />
      )}

      {/* Resource List Section and Save/Copy controls */}
      {showAssetSearch && (
        <div className="px-6">
          {!hideTabs && (
            <ResourceListSection
              resourceLists={resourceLists}
              activeResourceListId={activeResourceListId}
              onSelectResourceList={handleSelectResourceList}
              onCreateResourceList={handleCreateResourceList}
              onEditResourceList={handleEditResourceList}
              onDeleteResourceList={handleDeleteResourceList}
              onUpdateResourceListOrder={updateResourceListOrderHandler}
              onUpdateResourceListIcon={updateResourceListIcon}
              onUpdateResourceListColor={updateResourceListColor}
              onUpdateResourceList={handleUpdateResourceListField}
              onToggleFavourite={toggleFavourite}
              onUpdateFavouriteOrder={handleUpdateFavouriteOrder}
              isDirty={isResourceListDirty}
              onSave={handleUpdateResourceList}
            />
          )}
          {hideTabs && (
            <div className="flex items-center justify-end gap-2 py-4">
              <div
                className={`transition-all duration-300 ease-in-out ${
                  isResourceListDirty ? "w-16" : "w-0"
                }`}
              >
                <AnimatePresence mode="wait">
                  {isResourceListDirty && (
                    <motion.div
                      initial={{ opacity: 0, scale: 1, x: 8 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{
                        opacity: 0,
                        scale: 1,
                        x: 8,
                        transition: { duration: 0.15 },
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                        delay: 0.15,
                      }}
                      style={{ transformOrigin: "center" }}
                    >
                      <Button
                        color="indigo"
                        onClick={handleUpdateResourceList}
                        className="w-full"
                      >
                        Save
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Layout Grid */}
      <div className="flex h-full p-4 gap-4">
        {/* Left Column - Chart (grows to fill available space) */}
        <div className={clsx("rounded-xl flex-1 p-4 flex flex-col space-y-6 overflow-y-auto", WORKBENCH_SURFACE_CLASS)}>
          {/* Chart Container */}
          <div className="relative h-full">
            {config.type === PlaygroundType.StatisticsTable ? (
              <StatisticsTableArea
                selectedAssets={selectedAssets}
                assetDisplayNames={assetDisplayNames}
                assetMetadata={assetMetadata}
                chartSettings={chartSettings}
                periods={periods}
                isLoading={isLoading}
                isInitialized={paramsLoaded}
                onPeriodChange={setPeriod}
                onCustomDateChange={setCustomDateRange}
                onAddToDashboard={() => setShowAddWidgetModal(true)}
                columns={chartSettings.columns || []}
                showBenchmarks={chartSettings.showBenchmarks ?? false}
                riskFreeRate={chartSettings.riskFreeRate ?? 0.02}
                onRiskFreeRateChange={setRiskFreeRate}
                extendIncompleteWithProxy={chartSettings.extendIncompleteWithProxy ?? false}
              />
            ) : config.type === PlaygroundType.CorrelationMatrix ? (
              <CorrelationMatrixArea
                selectedAssets={selectedAssets}
                assetDisplayNames={assetDisplayNames}
                assetMetadata={assetMetadata}
                chartSettings={chartSettings}
                periods={periods}
                isLoading={isLoading}
                isInitialized={paramsLoaded}
                onPeriodChange={setPeriod}
                onCustomDateChange={setCustomDateRange}
                onAddToDashboard={() => setShowAddWidgetModal(true)}
              />
            ) : (
              <ChartArea
                playgroundType={config.type}
                selectedAssets={selectedAssets}
                assetDisplayNames={assetDisplayNames}
                assetMetadata={assetMetadata}
                assetColors={assetColors}
                chartSettings={chartSettings}
                visualSettings={visualSettings}
                exposureBands={exposureBands}
                periods={periods}
                data={data}
                assetStats={assetStats}
                isLoading={isLoading}
                isInitialized={paramsLoaded}
                onPeriodChange={setPeriod}
                onCustomDateChange={setCustomDateRange}
                onOpenVisualSettings={openVisualSettings}
                onAddToDashboard={() => setShowAddWidgetModal(true)}
              />
            )}
          </div>
        </div>

        {/* Right Panel with toggle button */}
        <div className="group flex">
          <AnimatePresence mode="wait" initial={false}>
            {!isRightPanelMinimized && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 320 }}
                exit={{ opacity: 0, width: 0 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0.0, 0.2, 1],
                }}
                className="flex-shrink-0 overflow-hidden relative flex"
              >
                <div className="flex items-center h-full">
                  <button
                    onClick={handleRightPanelToggle}
                    className={`opacity-0 rounded-l-xl group-hover:opacity-100 ${REPORT_BUILDER_PANEL_TOGGLE_CLASS}`}
                    aria-label="Hide settings panel"
                  >
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="w-3 h-3"
                    />
                  </button>
                </div>
                <div
                  className={clsx(
                    "rounded-2xl w-80 h-full flex flex-col overflow-y-auto divide-y divide-slate-200 dark:divide-slate-700/50",
                    WORKBENCH_INSET_PANEL_CLASS,
                  )}
                >
                  {/* Widget Config */}
                  <div className="p-4">
                    <WidgetConfig
              frequency={chartSettings.frequency}
              setFrequency={setFrequency}
              displayMode={chartSettings.displayMode}
              setDisplayMode={setDisplayMode}
              rebaseValue={chartSettings.rebaseValue}
              setRebaseValue={setRebaseValue}
              playgroundType={config.type}
              setPlaygroundType={(type) => {
                if (!isPlaygroundTypeAvailable(user?.company_id, type)) {
                  return;
                }
                setPlaygroundType(type);
                // When switching to Exposure type, ensure we have a default exposure type set
                if (
                  (type === PlaygroundType.Exposure ||
                    type === PlaygroundType.ExposureColumns ||
                    type === PlaygroundType.ExposureOverTime) &&
                  !chartSettings.exposureType
                ) {
                  setExposureType("asset_allocation");
                }
                if (type === PlaygroundType.ExposureOverTime) {
                  setFrequency("quarterly");
                }
              }}
              statistic={chartSettings.statistic as StatisticType}
              setStatistic={setStatistic}
              windowSize={chartSettings.windowSize}
              setWindowSize={setWindowSize}
              metricFieldPath={chartSettings.metricFieldPath}
              setMetricFieldPath={setMetricFieldPath}
              exposureType={chartSettings.exposureType}
              setExposureType={handleSetExposureType}
              exposureDisplayMode={chartSettings.exposureDisplayMode}
              setExposureDisplayMode={setExposureDisplayMode}
              exposureCategories={exposureCategories}
              exposureBands={exposureBands}
              onSaveExposureBands={handleSaveExposureBands}
              onClearExposureBands={clearExposureBands}
              selectedAssets={selectedAssets}
              assetMetadata={assetMetadata}
              defaultResourceType={defaultResourceType}
              xStatistic={chartSettings.xStatistic}
              setXStatistic={setXStatistic}
              yStatistic={chartSettings.yStatistic}
              setYStatistic={setYStatistic}
              columns={chartSettings.columns || []}
              setColumns={setColumns}
              showBenchmarks={chartSettings.showBenchmarks ?? false}
              setShowBenchmarks={setShowBenchmarks}
              extendIncompleteWithProxy={chartSettings.extendIncompleteWithProxy ?? false}
              setExtendIncompleteWithProxy={setExtendIncompleteWithProxy}
              riskFreeRate={chartSettings.riskFreeRate ?? 0.02}
              setRiskFreeRate={setRiskFreeRate}
            />
                  </div>

                  {/* Data Settings */}
                  {showAssetSearch && (
                    <div className="p-6">
                      <div className="text-lg font-medium mb-2 text-slate-900 dark:text-white">
                        Resources
                      </div>
                      <DataSettings
                        selectedAssets={selectedAssets}
                        toggleAsset={toggleAsset}
                        assetColors={assetColors}
                        setAssetColor={setAssetColor}
                        assetDisplayNames={assetDisplayNames}
                        isSearchOpen={isSearchOpen}
                        setIsSearchOpen={handleSetSearchOpen}
                        showColorPickers={true}
                        onSendToScreener={handleSendToScreener}
                        accessDeniedIds={accessDeniedIds}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {isRightPanelMinimized && (
            <div className="flex items-center h-full">
              <button
                onClick={handleRightPanelToggle}
                className={`rounded-xl ${REPORT_BUILDER_PANEL_TOGGLE_CLASS}`}
                aria-label="Show settings panel"
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className="w-3 h-3"
                />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Visual Settings Drawer */}
      <ChartVisualConfig
        showAreaGradient={visualSettings.showAreaGradient}
        setShowAreaGradient={(value) =>
          updateVisualSetting("showAreaGradient", value)
        }
        showCrosshair={visualSettings.showCrosshair}
        setShowCrosshair={(value) =>
          updateVisualSetting("showCrosshair", value)
        }
        showGridRows={visualSettings.showGridRows}
        setShowGridRows={(value) => updateVisualSetting("showGridRows", value)}
        showGridColumns={visualSettings.showGridColumns}
        setShowGridColumns={(value) =>
          updateVisualSetting("showGridColumns", value)
        }
        showPatternBackground={visualSettings.showPatternBackground}
        setShowPatternBackground={(value) =>
          updateVisualSetting("showPatternBackground", value)
        }
        smoothLines={visualSettings.smoothLines}
        setSmoothLines={(value) => updateVisualSetting("smoothLines", value)}
        showXAxis={visualSettings.showXAxis}
        setShowXAxis={(value) => updateVisualSetting("showXAxis", value)}
        showYAxis={visualSettings.showYAxis}
        setShowYAxis={(value) => updateVisualSetting("showYAxis", value)}
        isOpen={isVisualSettingsOpen}
        onClose={closeVisualSettings}
        resetToDefaults={resetVisualSettingsToDefaults}
      />

      {/* Add Widget Modal */}
      <AddWidgetModal
        isOpen={showAddWidgetModal}
        onClose={() => setShowAddWidgetModal(false)}
        widgetType="playground"
        initialConfig={{
          queryId: `playground-${Date.now()}`,
          chartType:
            config.type === PlaygroundType.RollingStats ? "bar" : "line",
          assets: selectedAssets,
          assetDisplayNames,
          assetMetadata,
          assetColors,
          period: chartSettings.period,
          displayMode: chartSettings.displayMode,
          rebaseValue: chartSettings.rebaseValue,
          frequency: chartSettings.frequency,
          statistic: chartSettings.statistic,
          windowSize: chartSettings.windowSize,
          customDateRange: chartSettings.customDateRange,
          visualSettings: {
            showAreaGradient: visualSettings.showAreaGradient,
            showGlyphs: visualSettings.showGlyphs,
            showCrosshair: visualSettings.showCrosshair,
            showGridRows: visualSettings.showGridRows,
            showGridColumns: visualSettings.showGridColumns,
            showPatternBackground: visualSettings.showPatternBackground,
            smoothLines: visualSettings.smoothLines,
            showXAxis: visualSettings.showXAxis,
            showYAxis: visualSettings.showYAxis,
          },
          playgroundType: config.type,
          stream_type:
            (selectedAssets.length > 0 &&
              assetMetadata[selectedAssets[0]]?.streamType) ||
            "default",
          ...(config.type === PlaygroundType.Exposure ||
          config.type === PlaygroundType.ExposureColumns
            ? {
                exposureType: chartSettings.exposureType,
                combineVariants: chartSettings.combineVariants,
              }
            : {}),
          ...(config.type === PlaygroundType.ExposureOverTime
            ? {
                exposureType: chartSettings.exposureType,
                exposureDisplayMode: chartSettings.exposureDisplayMode,
                frequency: chartSettings.frequency,
              }
            : {}),
          ...(config.type === PlaygroundType.ScatterChart && {
            xStatistic: chartSettings.xStatistic,
            yStatistic: chartSettings.yStatistic,
            xLabel: chartSettings.xLabel,
            yLabel: chartSettings.yLabel,
          }),
          ...(config.type === PlaygroundType.StatisticsTable && {
            columns: chartSettings.columns,
            showBenchmarks: chartSettings.showBenchmarks,
          }),
        }}
      />

      {/* Resource List Dialogs */}
      <AddResourceListDialog
        isOpen={isResourceListDialogOpen}
        onClose={() => {
          setIsResourceListDialogOpen(false);
          setNewResourceListName("");
          setUseCurrentResources(true);
        }}
        resourceListName={newResourceListName}
        setResourceListName={setNewResourceListName}
        useCurrentResources={useCurrentResources}
        setUseCurrentResources={setUseCurrentResources}
        hasCurrentResources={selectedAssets.length > 0}
        onAddResourceList={handleAddResourceList}
        inputRef={addResourceListInputRef}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          if (pendingDeleteId) {
            deleteResourceListById(pendingDeleteId);
          }
          setDeleteDialogOpen(false);
          setPendingDeleteId(null);
        }}
      />
    </div>
  );
};

export default Playground;
